import { ChevronDown, ChevronUp, Plus, UserMinus, UserPlus } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ErrorState from '../components/common/ErrorState';
import Modal from '../components/common/Modal';
import PageHeader from '../components/common/PageHeader';
import SkeletonList from '../components/common/SkeletonList';
import { useApps } from '../contexts/AppsContext';
import groupsService from '../services/groups';
import usersService from '../services/users';
import { resolveDefaultAppId, setSelectedAppId } from '../utils/storage';

const GroupsPage = () => {
    const { apps, loading: loadingApps } = useApps();
    const [appId, setAppId] = useState('');
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [searchParams] = useSearchParams();

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showAddMemberModal, setShowAddMemberModal] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [members, setMembers] = useState([]);
    const [membersLoading, setMembersLoading] = useState(false);
    const [expandedGroup, setExpandedGroup] = useState(null);
    const [allUsers, setAllUsers] = useState([]);

    const [formData, setFormData] = useState({
        group_name: '',
        group_type: 'team',
        description: '',
        parent_group_id: '',
    });
    const [memberFormData, setMemberFormData] = useState({
        user_id: '',
        role_in_group: 'member',
    });

    useEffect(() => {
        if (apps?.length) {
            const id = resolveDefaultAppId(apps, searchParams.get('app_id'));
            setAppId(id);
            loadGroups(id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apps, searchParams]);

    const loadGroups = async (id) => {
        setLoading(true);
        setError('');
        try {
            const data = await groupsService.getAll(id || appId);
            setGroups(data || []);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to load groups.');
        } finally {
            setLoading(false);
        }
    };

    const loadMembers = async (groupId) => {
        setMembersLoading(true);
        try {
            const data = await groupsService.getMembers(groupId, appId);
            setMembers(data || []);
        } catch {
            setMembers([]);
        } finally {
            setMembersLoading(false);
        }
    };

    const handleCreateGroup = async () => {
        try {
            await groupsService.create({ ...formData, app_id: appId });
            setSuccessMessage('Group created successfully.');
            setShowCreateModal(false);
            setFormData({ group_name: '', group_type: 'team', description: '', parent_group_id: '' });
            loadGroups(appId);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to create group.');
        }
    };

    const handleToggleStatus = async (group) => {
        try {
            await groupsService.updateStatus(group.group_id, group.is_active ? 0 : 1);
            setSuccessMessage(group.is_active ? 'Group deactivated.' : 'Group activated.');
            loadGroups(appId);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to update group.');
        }
    };

    const handleExpandGroup = async (groupId) => {
        if (expandedGroup === groupId) {
            setExpandedGroup(null);
            return;
        }
        setExpandedGroup(groupId);
        loadMembers(groupId);
    };

    const handleAddMember = async () => {
        try {
            await groupsService.addMember({
                group_id: selectedGroup.group_id,
                user_id: memberFormData.user_id,
                role_in_group: memberFormData.role_in_group,
            });
            setSuccessMessage('Member added.');
            setShowAddMemberModal(false);
            setMemberFormData({ user_id: '', role_in_group: 'member' });
            loadMembers(selectedGroup.group_id);
            loadGroups(appId);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to add member.');
        }
    };

    const handleRemoveMember = async (userId) => {
        try {
            await groupsService.removeMember(expandedGroup, userId);
            setSuccessMessage('Member removed.');
            loadMembers(expandedGroup);
            loadGroups(appId);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to remove member.');
        }
    };

    const openAddMember = async (group) => {
        setSelectedGroup(group);
        setShowAddMemberModal(true);
        try {
            const users = await usersService.getAllActiveUsersAcrossApps();
            setAllUsers(users || []);
        } catch {
            setAllUsers([]);
        }
    };

    useEffect(() => {
        if (successMessage) {
            const t = setTimeout(() => setSuccessMessage(''), 3000);
            return () => clearTimeout(t);
        }
    }, [successMessage]);

    if (loadingApps) return <SkeletonList />;

    return (
        <div className="space-y-6">
            <PageHeader
                title="Groups"
                subtitle="Manage user groups and team memberships"
                actions={
                    <div className="flex items-center gap-3">
                        <select
                            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                            value={appId}
                            onChange={(e) => {
                                setAppId(e.target.value);
                                setSelectedAppId(e.target.value);
                                loadGroups(e.target.value);
                            }}
                        >
                            {apps?.map((a) => (
                                <option key={a.app_id} value={a.app_id}>
                                    {a.app_name}
                                </option>
                            ))}
                        </select>
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                        >
                            <Plus size={16} /> Create Group
                        </button>
                    </div>
                }
            />

            {successMessage && (
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-700">
                    {successMessage}
                </div>
            )}
            {error && (
                <div>
                    <ErrorState message={error} />
                    <button
                        onClick={() => {
                            setError('');
                            loadGroups(appId);
                        }}
                        className="mt-2 text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                    >
                        Retry
                    </button>
                </div>
            )}

            {loading ? (
                <SkeletonList />
            ) : (
                <div className="space-y-3">
                    {groups.length === 0 ? (
                        <div className="rounded-2xl border border-slate-200 bg-white/90 backdrop-blur p-4 shadow-sm">
                            <p className="text-slate-500 text-center py-8">
                                No groups found. Create one to get started.
                            </p>
                        </div>
                    ) : (
                        groups.map((g) => (
                            <div
                                key={g.group_id}
                                className="rounded-2xl border border-slate-200 bg-white/90 backdrop-blur p-4 shadow-sm hover:shadow-md transition"
                            >
                                <div className="flex items-center justify-between">
                                    <div
                                        className="flex items-center gap-3 cursor-pointer"
                                        onClick={() => handleExpandGroup(g.group_id)}
                                    >
                                        {expandedGroup === g.group_id ? (
                                            <ChevronUp size={16} />
                                        ) : (
                                            <ChevronDown size={16} />
                                        )}
                                        <div>
                                            <h3 className="font-medium text-slate-900">{g.group_name}</h3>
                                            <p className="text-xs text-slate-500">
                                                {g.group_type} &middot; {g.member_count} member
                                                {g.member_count !== 1 ? 's' : ''}
                                                {g.parent_group_name && <> &middot; Parent: {g.parent_group_name}</>}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => openAddMember(g)}
                                            className="p-1.5 rounded hover:bg-slate-100"
                                            title="Add Member"
                                        >
                                            <UserPlus size={16} className="text-indigo-600" />
                                        </button>
                                        <button
                                            onClick={() => handleToggleStatus(g)}
                                            className={`px-2 py-1 rounded text-xs font-medium ${g.is_active
                                                ? 'bg-emerald-100 text-emerald-700'
                                                : 'bg-red-100 text-red-700'
                                                }`}
                                        >
                                            {g.is_active ? 'Active' : 'Inactive'}
                                        </button>
                                    </div>
                                </div>
                                {g.description && <p className="text-sm text-slate-600 mt-1 ml-7">{g.description}</p>}

                                {expandedGroup === g.group_id && (
                                    <div className="mt-3 ml-7 border-t pt-3">
                                        {membersLoading ? (
                                            <p className="text-sm text-slate-400">Loading members...</p>
                                        ) : members.length === 0 ? (
                                            <p className="text-sm text-slate-400">No members yet.</p>
                                        ) : (
                                            <table className="w-full text-sm">
                                                <thead>
                                                    <tr className="text-left text-slate-500 border-b">
                                                        <th className="py-1 font-medium">Name</th>
                                                        <th className="py-1 font-medium">Email</th>
                                                        <th className="py-1 font-medium">Role</th>
                                                        <th className="py-1 font-medium">Since</th>
                                                        <th></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {members.map((m) => (
                                                        <tr key={m.membership_id} className="border-b border-slate-50">
                                                            <td className="py-1.5">
                                                                {m.first_name} {m.last_name}
                                                            </td>
                                                            <td className="py-1.5 text-slate-500">{m.email_id}</td>
                                                            <td className="py-1.5">
                                                                <span className="px-2 py-0.5 rounded bg-slate-100 text-xs">
                                                                    {m.role_in_group}
                                                                </span>
                                                            </td>
                                                            <td className="py-1.5 text-slate-500">
                                                                {m.effective_from?.slice(0, 10)}
                                                            </td>
                                                            <td className="py-1.5 text-right">
                                                                <button
                                                                    onClick={() => handleRemoveMember(m.user_id)}
                                                                    className="p-1 rounded hover:bg-red-50"
                                                                    title="Remove"
                                                                >
                                                                    <UserMinus size={14} className="text-red-500" />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            )}

            {/* Create Group Modal */}
            <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="Create Group">
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Group Name *</label>
                        <input
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                            value={formData.group_name}
                            onChange={(e) => setFormData({ ...formData, group_name: e.target.value })}
                            placeholder="e.g., Cardiology Team"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
                        <select
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                            value={formData.group_type}
                            onChange={(e) => setFormData({ ...formData, group_type: e.target.value })}
                        >
                            <option value="team">Team</option>
                            <option value="department">Department</option>
                            <option value="region">Region</option>
                            <option value="care_team">Care Team</option>
                            <option value="pod">Pod</option>
                            <option value="custom">Custom</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                        <textarea
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                            rows={2}
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Parent Group</label>
                        <select
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                            value={formData.parent_group_id}
                            onChange={(e) => setFormData({ ...formData, parent_group_id: e.target.value })}
                        >
                            <option value="">None</option>
                            {groups.map((g) => (
                                <option key={g.group_id} value={g.group_id}>
                                    {g.group_name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                        <button
                            onClick={() => setShowCreateModal(false)}
                            className="px-4 py-2 text-sm rounded-lg border border-slate-300 hover:bg-slate-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleCreateGroup}
                            disabled={!formData.group_name}
                            className="px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                        >
                            Create
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Add Member Modal */}
            <Modal
                isOpen={showAddMemberModal}
                onClose={() => setShowAddMemberModal(false)}
                title={`Add Member to ${selectedGroup?.group_name || ''}`}
            >
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">User</label>
                        <select
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                            value={memberFormData.user_id}
                            onChange={(e) => setMemberFormData({ ...memberFormData, user_id: e.target.value })}
                        >
                            <option value="">Select user...</option>
                            {allUsers.map((u) => (
                                <option key={u.user_id} value={u.user_id}>
                                    {u.first_name} {u.last_name} ({u.email_id})
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Role in Group</label>
                        <select
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                            value={memberFormData.role_in_group}
                            onChange={(e) => setMemberFormData({ ...memberFormData, role_in_group: e.target.value })}
                        >
                            <option value="member">Member</option>
                            <option value="lead">Lead</option>
                            <option value="supervisor">Supervisor</option>
                            <option value="observer">Observer</option>
                        </select>
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                        <button
                            onClick={() => setShowAddMemberModal(false)}
                            className="px-4 py-2 text-sm rounded-lg border border-slate-300 hover:bg-slate-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleAddMember}
                            disabled={!memberFormData.user_id}
                            className="px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                        >
                            Add Member
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default GroupsPage;
